// UI-2: PageLayout, PageHeader and the list (List + ListRow).
// design-brief.md section 2 (the page pattern); mobile-responsive-addendum.md 4 (a page on
// a phone) and 5 (lists become stacked card rows; sorting becomes a "Sort" chip and sheet).
import React from 'react';
import { v } from './tokens';
import { useBreakpoint } from './breakpoints';
import { HeaderButton } from './Button';
import { OverflowMenu } from './Popover';
import HelpButton from '../tours/HelpButton';
import './v2.css';

// Padding, max width and the glow. On a phone the bottom padding leaves room for the
// floating Ask button and the home indicator (--page-pad, tokens.css).
export function PageLayout({ children, as: Tag = 'div', ...rest }) {
  return (
    <Tag className="ui-page" {...rest}>
      <div className="ui-page__inner">{children}</div>
    </Tag>
  );
}

// eyebrow: { label, color } (the dot is the page's sidebar group colour).
// primary: the page's main action. Decision 88 (Carla, rewritten 2026-10-21): the actions next to a
// page title are BUTTONS: the primary one the green create button (<HeaderButton primary> or
// <AddTaskButton>), at most one; the others secondary buttons. A test that discovers every header in
// src fails on a text link here. Controls that are not actions (a search box, a period switch) may
// sit here too. secondary: [{ label, onClick }], secondary buttons beside it; on a phone ONE stays a
// button, two or more move into a 44px ⋯ menu (addendum 4) whose trigger is a secondary button too.
// help: the page's guide id (tours/guides.js). Decision 86: the ? sits straight after the H1,
// centred on it, on every page; it is never one of the header actions. A page with no guide
// passes nothing and shows no ?.
export function PageHeader({ eyebrow, title, subline, primary, secondary = [], help }) {
  const phone = useBreakpoint() === 'phone';
  return (
    <header className="ui-page-header">
      <div className="ui-page-header__text">
        {eyebrow && (
          <span className="ui-eyebrow">
            <span className="ui-eyebrow__dot" style={{ '--ui-bg': v(eyebrow.color || 'pastel-neutral') }} aria-hidden="true" />
            {eyebrow.label}
          </span>
        )}
        <div className="ui-page-header__titlerow">
          <h1 className="ui-h1">{title}</h1>
          {help && <span className="ui-page-header__help"><HelpButton tourId={help} /></span>}
        </div>
        {subline && <p className="ui-subline">{subline}</p>}
      </div>
      {(primary || secondary.length > 0) && (
        <div className="ui-page-header__actions">
          {primary}
          {phone && secondary.length > 1
            ? <OverflowMenu label="More actions" buttonClassName="ui-btn ui-btn--secondary ui-page-header__more" items={secondary.map((s) => ({ label: s.label, onSelect: s.onClick }))} />
            : secondary.map((s) => <HeaderButton key={s.label} onClick={s.onClick}>{s.label}</HeaderButton>)}
        </div>
      )}
    </header>
  );
}

// columns: [{ key, label, width = 'minmax(0, 1fr)', sortable?, role?, truncate? }]. truncate keeps a
// long value (an email) on one line with an ellipsis, the whole value in its title. role marks the
// cells a phone row keeps: 'primary' (line 1, left), 'status' (line 1, right) and 'actions'.
// sort: { key, dir: 'asc' | 'desc' }. On a phone the header is hidden and sorting moves
// into a "Sort" chip that opens a sheet.
export function List({ label, columns, sort, onSortChange, children }) {
  const phone = useBreakpoint() === 'phone';
  const cols = columns.map((c) => c.width || 'minmax(0, 1fr)').join(' ');
  const sortable = columns.filter((c) => c.sortable);
  const sortLabel = sort ? `${columns.find((c) => c.key === sort.key)?.label || sort.key} ${sort.dir === 'desc' ? '↓' : '↑'}` : 'Sort';
  const nextSort = (key) => ({ key, dir: sort?.key === key && sort.dir === 'asc' ? 'desc' : 'asc' });

  return (
    <div className="ui-list-wrap" style={{ '--ui-cols': cols, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {phone && sortable.length > 0 && onSortChange && (
        <div className="ui-list__toolbar">
          <OverflowMenu title="Sort by" label={`Sort: ${sortLabel}`} buttonClassName="ui-chip ui-hit"
            items={sortable.flatMap((c) => [
              { label: `${c.label}, A to Z`, checked: sort?.key === c.key && sort.dir === 'asc', onSelect: () => onSortChange({ key: c.key, dir: 'asc' }) },
              { label: `${c.label}, Z to A`, checked: sort?.key === c.key && sort.dir === 'desc', onSelect: () => onSortChange({ key: c.key, dir: 'desc' }) },
            ])}>
            <span aria-hidden="true">⇅</span> Sort: {sortLabel}
          </OverflowMenu>
        </div>
      )}
      <div className="ui-list" role="list" aria-label={label}>
        <div className="ui-list__head" aria-hidden={phone ? 'true' : undefined}>
          {columns.map((c) => (
            <span key={c.key} className="ui-list__th" style={c.align === 'end' ? { textAlign: 'right' } : undefined}
              aria-sort={sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
              {c.sortable && onSortChange ? (
                <button type="button" className="ui-list__sort" onClick={() => onSortChange(nextSort(c.key))}>
                  {c.label}{sort?.key === c.key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                </button>
              ) : c.label}
            </span>
          ))}
        </div>
        {React.Children.map(children, (child) => (child ? React.cloneElement(child, { columns }) : child))}
      </div>
    </div>
  );
}

// cells: { [columnKey]: node }. meta: line 2 on a phone (the two most useful values).
// onOpen: opens the detail drawer (row tap; the name is the keyboard target).
export function ListRow({ columns = [], cells, meta, onOpen, openLabel }) {
  const roleOf = (c) => c.role || (c.key === 'actions' ? 'actions' : null);
  return (
    <div className="ui-row" role="listitem" onClick={onOpen ? (e) => { if (!e.target.closest('button, a, input, [role="menu"]')) onOpen(); } : undefined}>
      {columns.map((c) => {
        const role = roleOf(c);
        const cls = ['ui-row__cell', role && `ui-row__cell--${role}`, c.truncate && 'ui-row__cell--truncate'].filter(Boolean).join(' ');
        let content = cells[c.key];
        if (role === 'primary' && onOpen) {
          content = (
            <button type="button" className="ui-list__sort" style={{ margin: 0, padding: 0, textAlign: 'left', width: '100%' }}
              aria-label={openLabel} onClick={onOpen}>
              {content}
            </button>
          );
        }
        return (
          <div key={c.key} className={cls} title={c.truncate && typeof content === 'string' ? content : undefined} style={c.align === 'end' && role !== 'status' && role !== 'actions' ? { textAlign: 'right' } : role === 'actions' ? { justifyContent: 'flex-end', display: 'flex' } : undefined}>
            {content}
          </div>
        );
      })}
      {meta && <div className="ui-row__meta">{meta}</div>}
    </div>
  );
}

// The primary cell's content: avatar or icon, the name, a sub line.
export function RowPrimary({ leading, name, sub }) {
  return (
    <span className="ui-row__primary">
      {leading}
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span className="ui-row__name">{name}</span>
        {sub && <span className="ui-row__sub">{sub}</span>}
      </span>
    </span>
  );
}
